// Mutation hook for POST /api/sites/:siteId/comms/channels.
//
// On success, invalidates the channels-list cache for this site so the
// sidebar re-renders with the new entry.

import { useMutation, useQueryClient, type MutationMeta } from '@tanstack/react-query';
import type { CreateChannelBody, CreateChannelResponse } from '../../apis/comms.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { commsKeys } from '../queryKeys.js';

/** `meta`: the mutation's react-query meta — `OWNS_ERROR_FEEDBACK` when the caller reports a failure itself. */
export function useCreateChannel(siteId: string | null, meta?: MutationMeta) {
  const { createChannel } = useClientApis().comms;
  const queryClient = useQueryClient();
  return useMutation<CreateChannelResponse, Error, CreateChannelBody>({
    meta,
    mutationFn: (body) => {
      if (!siteId) throw new Error('siteId is required to create a channel');
      return createChannel(siteId, body);
    },
    onSuccess: () => {
      // Refetch the channel list so the sidebar shows the new channel.
      void queryClient.invalidateQueries({ queryKey: commsKeys.channels(siteId ?? '') });
    },
  });
}
